import { useState, useEffect, useRef } from "react";
import { SimulatedLoading } from "./SimulatedLoading";

interface LazyRevealSectionProps {
  fallback: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

export function LazyRevealSection({
  fallback,
  children,
  delay = 500,
  className = ""
}: LazyRevealSectionProps) {
  const [hasIntersected, setHasIntersected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasIntersected) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setHasIntersected(true);
        }
      },
      {
        rootMargin: "0px 0px -50px 0px",
        threshold: 0.05
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasIntersected]);

  return (
    <div ref={containerRef} className={`lazy-section-wrapper w-full ${className}`}>
      {hasIntersected ? (
        <SimulatedLoading fallback={fallback} delay={delay}>
          {children}
        </SimulatedLoading>
      ) : (
        <div className="w-full opacity-40">{fallback}</div>
      )}
    </div>
  );
}

export default LazyRevealSection;
